/**
 * Slides (Module) API — typed wrapper around /v3/slides endpoints.
 *
 * In the Parse backend a "Slide" = a Module (content leaf in the course tree).
 *
 * Confirmed endpoints from legacy SlidesAPI.js:
 *   GET    /v3/slides/short/all              → all user's modules (short)
 *   GET    /v3/slides/:id                    → full module (with text/images/items)
 *   GET    /v3/container/:containerId/slides → modules in a specific container
 *   POST   /v3/slides?containerId=xxx        → create module inside container
 *   PUT    /v3/slides                        → update module
 *   DELETE /v3/slides/:id                    → delete module
 */
import apiClient from './client';
import { invalidateContainersCache } from './containers.api';
import type { ContentBlock } from '../features/courses/components/ModuleContentEditor';

/* ── Types ─────────────────────────────────────────────────────────────────── */

/* ── Section types ─────────────────────────────────────────────────── */

export type FixedSectionId = 'intro' | 'theory' | 'practice' | 'check' | 'homework';

export const FIXED_SECTIONS: { id: FixedSectionId; label: string; icon: string; color: string }[] = [
  { id: 'intro',    label: 'Введение',  icon: '📖', color: '#0891b2' },
  { id: 'theory',   label: 'Материал',   icon: '📚', color: '#7c3aed' },
  { id: 'practice', label: 'Практика',   icon: '✏️', color: '#d97706' },
  { id: 'check',    label: 'Тест',       icon: '📝', color: '#dc2626' },
  { id: 'homework', label: 'ДЗ',         icon: '📌', color: '#059669' },
];

export interface ModuleSection {
  id: string;                 // FixedSectionId or UUID
  label: string;
  description?: string;       // Custom instructions/note for the section
  icon?: string;
  color?: string;
  order: number;
  enabled: boolean;           // hidden sections don't render
}

export type SectionMode = 'none' | 'fixed' | 'free';

export interface SlideModule {
  id: string;
  name?: string;
  description?: string;
  studentTask?: string;
  teacherInstructions?: string;
  access?: 'private' | 'group' | 'free' | 'paid';
  avatar?: string;
  imageUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  text?: string;           // legacy plain text content
  richText?: string;       // Lexical JSON content (new)
  /** Structured content blocks (serialised as JSON string in 'contentBlocksJson' or directly) */
  contentBlocks?: ContentBlock[];
  copyrightText?: string;
  /** Section mode for this module */
  sectionMode?: SectionMode;
  /** Section definitions (fixed: subset of FIXED_SECTIONS; free: custom list) */
  sections?: ModuleSection[];
  /** Track student progress per section (show ✅/⚪ indicators) */
  trackProgress?: boolean;
  /** Lock next section until previous one is completed */
  lockSections?: boolean;
  isDeleted?: boolean;
  ownerId?: string;
  creatorId?: string;
  timestamp?: number;
  updatedTimestamp?: number;
  [key: string]: unknown;
}

/* ── Client-side slide cache ─────────────────────────────────────────────── */
// getByContainer returns full slide data (including items[]). Cache it here
// so that getById can serve it without hitting /v3/slides/:id which may 404.
const slideCache = new Map<string, SlideModule>();

// IDs that are lesson tasks (not course slides) — skip /v3/slides/ for these.
const taskIds = new Set<string>();

/* ── sessionStorage cache for getByContainer (SLIM — tree data only) ─────── */
// Stores only {id, name, isDeleted} to stay well within the ~5MB sessionStorage limit.
// Full slide content is loaded on-demand when the editor opens (via getById).
const SLIDE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const SLIDE_CACHE_PREFIX = 'ep_slides_';

interface SlideSlim { id: string; name?: string; isDeleted?: boolean; }

function readSlideSessionCache(containerId: string): SlideModule[] | null {
  try {
    const raw = sessionStorage.getItem(SLIDE_CACHE_PREFIX + containerId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { data: SlideSlim[]; ts: number };
    if (Date.now() - parsed.ts > SLIDE_CACHE_TTL) {
      sessionStorage.removeItem(SLIDE_CACHE_PREFIX + containerId);
      return null;
    }
    // Return slim objects cast as SlideModule[] — tree only needs id + name
    return parsed.data as unknown as SlideModule[];
  } catch { return null; }
}

function writeSlideSessionCache(containerId: string, data: SlideModule[]) {
  try {
    // Only store fields needed for tree rendering (id + name + isDeleted)
    const slim: SlideSlim[] = data.map(s => ({
      id: s.id,
      name: s.name,
      isDeleted: s.isDeleted,
    }));
    sessionStorage.setItem(
      SLIDE_CACHE_PREFIX + containerId,
      JSON.stringify({ data: slim, ts: Date.now() })
    );
  } catch { /* sessionStorage still full after slimming — skip */ }
}

export function invalidateSlidesCache(containerId?: string) {
  if (containerId) {
    try { sessionStorage.removeItem(SLIDE_CACHE_PREFIX + containerId); } catch { /* ignore */ }
  } else {
    try {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith(SLIDE_CACHE_PREFIX))
        .forEach(k => sessionStorage.removeItem(k));
    } catch { /* ignore */ }
  }
}

// On module load: clear any old "fat" cache entries that contain full slide objects.
// These were stored by a previous version and are too large for sessionStorage.
try {
  Object.keys(sessionStorage)
    .filter(k => k.startsWith(SLIDE_CACHE_PREFIX))
    .forEach(k => {
      try {
        const raw = sessionStorage.getItem(k);
        if (!raw) return;
        const parsed = JSON.parse(raw) as { data: unknown[]; ts: number };
        // If entries have more than 3 keys, they're "fat" objects — remove them
        const sample = parsed.data?.[0] as any;
        if (sample && Object.keys(sample).length > 3) {
          sessionStorage.removeItem(k);
        }
      } catch { sessionStorage.removeItem(k); }
    });
} catch { /* ignore */ }

function hasFullContent(slide: SlideModule): boolean {
  if (!slide || typeof slide !== 'object') return false;
  if ((slide as any)._isTaskSeed) return false;
  const blocks = (slide.contentBlocks as any[]) ?? [];
  if (blocks.length > 0) return true;
  const items = (slide.items as any[]) ?? [];
  if (items.length > 1) return true;
  if (items.length === 1) {
    const first = items[0];
    if (first && (first.cards?.length > 0 || first.questions?.length > 0 || first.content || first.richText || first.answerType || first.url || first.imageUrl)) {
      return true;
    }
    return false;
  }
  return !!(
    slide.richText ||
    slide.text ||
    (Array.isArray(slide.homeworkBlocks) && (slide.homeworkBlocks as any[]).length > 0) ||
    (Array.isArray(slide.elements) && (slide.elements as any[]).length > 0) ||
    (Array.isArray(slide.images) && (slide.images as any[]).length > 0) ||
    (Array.isArray(slide.photos) && (slide.photos as any[]).length > 0) ||
    slide.videoUrl ||
    slide.imageUrl ||
    slide.avatar ||
    slide.audioUrl ||
    slide.sources
  );
}

/* ── API ─────────────────────────────────────────────────────────────────── */

const SlidesAPI = {
  /** Short list of ALL user's modules (no content, fast) */
  async getAll(): Promise<SlideModule[]> {
    const { data } = await apiClient.get<SlideModule[]>('/v3/slides/short/all');
    const result = (data ?? []) as SlideModule[];
    return result.filter(x => !x.isDeleted);
  },

  /** Modules that have been soft-deleted (trash bin) */
  async getDeleted(): Promise<SlideModule[]> {
    const { data } = await apiClient.get<SlideModule[]>('/v3/slides/my-deleted');
    return (data ?? []) as SlideModule[];
  },

  /** Full module including content (text, images, contentBlocks) */
  async getById(id: string): Promise<SlideModule> {
    // ① Check cache first — populated by getByContainer or seedFromTask
    // ONLY use cache if entry actually contains full module content
    if (slideCache.has(id)) {
      const cached = slideCache.get(id)!;
      if (cached && (cached.id || cached._id) && !(cached as any).code && !(cached as any).error && hasFullContent(cached)) {
        return cached;
      }
    }

    // ② Known lesson tasks or explicit task prefixes — try /v3/tasks/:id first
    if (taskIds.has(id) || id.startsWith('t_') || id.startsWith('task_')) {
      try {
        const { data: rawTask } = await apiClient.get<any>(`/v3/tasks/${id}`);
        const taskData = (rawTask as any)?.result ?? rawTask;
        if (taskData && (taskData.id || taskData._id) && !taskData.code && !taskData.error) {
          const normalized = { ...taskData, id: taskData.id || taskData._id };
          taskIds.add(id);
          slideCache.set(id, normalized);
          return normalized;
        }
      } catch { /* fall through */ }
    }

    // ③ Try /v3/slides/:id (course modules)
    try {
      const { data: rawSlide } = await apiClient.get<any>(`/v3/slides/${id}`);
      const slideData = (rawSlide as any)?.result ?? rawSlide;
      if (slideData && (slideData.id || slideData._id) && !slideData.code && !slideData.error && (slideData.name || slideData.items || slideData.contentBlocks)) {
        const normalized = { ...slideData, id: slideData.id || slideData._id };
        slideCache.set(id, normalized);
        return normalized;
      }
    } catch {
      // fall through to task endpoint
    }

    // ④ Fall back to /v3/tasks/:id (lesson tasks are also slide-like objects)
    try {
      const { data: rawTaskFallback } = await apiClient.get<any>(`/v3/tasks/${id}`);
      const taskFallback = (rawTaskFallback as any)?.result ?? rawTaskFallback;
      if (taskFallback && (taskFallback.id || taskFallback._id) && !taskFallback.code && !taskFallback.error) {
        const normalizedFallback = { ...taskFallback, id: taskFallback.id || taskFallback._id };
        taskIds.add(id);
        slideCache.set(id, normalizedFallback);
        return normalizedFallback;
      }
    } catch { /* fall through */ }

    throw new Error('Module not found');
  },

  /**
   * Pre-populate the cache from already-fetched task data.
   * Call this before opening ModuleEditorDrawer or TaskItemViewer with a task.id,
   * so getById returns content immediately without a network round-trip.
   */
  seedFromTask(task: { id?: string; _id?: string; name?: string; items?: unknown[]; slideId?: string; moduleId?: string; [key: string]: unknown }): void {
    if (!task) return;
    const id = (task.id || task._id) as string | undefined;
    if (!id) return;
    taskIds.add(id);
    if (!Array.isArray(task.contentBlocks) || task.contentBlocks.length === 0) {
      slideCache.set(id, { ...(task as any), id, _isTaskSeed: true });
    } else {
      slideCache.set(id, { ...(task as any), id });
    }
  },

  /** All modules inside a specific container/folder — also populates the cache */
  async getByContainer(containerId: string): Promise<SlideModule[]> {
    // Check sessionStorage cache first (survives HMR / page reload)
    const cached = readSlideSessionCache(containerId);
    if (cached) {
      cached.forEach(s => {
        if (hasFullContent(s)) slideCache.set(s.id, s);
      });
      return cached;
    }
    const { data } = await apiClient.get<SlideModule[]>(
      `/v3/container/${containerId}/slides`
    );
    const result = (data ?? []) as SlideModule[];
    const active = result.filter(x => !x.isDeleted);
    // Populate in-memory cache for full objects only and sessionStorage
    active.forEach(s => {
      if (hasFullContent(s)) slideCache.set(s.id, s);
    });
    writeSlideSessionCache(containerId, active);
    return active;
  },

  /** Create a new module inside a container */
  async create(
    payload: Partial<SlideModule>,
    containerId?: string
  ): Promise<SlideModule> {
    const { data } = await apiClient.post<SlideModule>(
      '/v3/slides',
      payload,
      containerId ? { params: { containerId } } : undefined
    );
    invalidateContainersCache();
    invalidateSlidesCache(containerId);
    return data;
  },

  /** Update module meta and/or content */
  async update(
    payload: Partial<SlideModule> & { id: string }
  ): Promise<SlideModule> {
    const normalize = (resData: any) => {
      const obj = (resData as any)?.result ?? resData;
      return (obj && typeof obj === 'object') ? obj : {};
    };

    // Known lesson task — skip /v3/slides/ and go straight to /v3/tasks/:id
    if (taskIds.has(payload.id)) {
      const { data } = await apiClient.put<SlideModule>(
        `/v3/tasks/${payload.id}`,
        { ...payload, hasChanges: true }
      );
      const resObj = normalize(data);
      const merged = { ...(slideCache.get(payload.id) ?? {}), ...payload, ...resObj };
      slideCache.set(payload.id, merged);
      invalidateContainersCache();
      invalidateSlidesCache();
      return merged;
    }
    try {
      const { data } = await apiClient.put<SlideModule>(
        '/v3/slides',
        { ...payload, hasChanges: true }
      );
      const resObj = normalize(data);
      const merged = { ...(slideCache.get(payload.id) ?? {}), ...payload, ...resObj };
      slideCache.set(payload.id, merged);
      invalidateContainersCache();
      invalidateSlidesCache();
      return merged;
    } catch (err: any) {
      // If the slides endpoint returns 404, this may be a lesson task (not a slide).
      // Fall back to the tasks endpoint and remember this ID for future calls.
      if (err?.response?.status === 404) {
        taskIds.add(payload.id);
        const { data } = await apiClient.put<SlideModule>(
          `/v3/tasks/${payload.id}`,
          { ...payload, hasChanges: true }
        );
        const resObj = normalize(data);
        const merged = { ...(slideCache.get(payload.id) ?? {}), ...payload, ...resObj };
        slideCache.set(payload.id, merged);
        invalidateContainersCache();
        invalidateSlidesCache();
        return merged;
      }
      throw err;
    }
  },

  /** Delete a module */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/v3/slides/${id}`, { data: { id } });
    invalidateContainersCache();
    invalidateSlidesCache();
  },

  /** List backups of a module */
  async getBackups(slideId: string): Promise<SlideBackup[]> {
    const { data } = await apiClient.get<SlideBackup[]>(
      `/v3/slides/${slideId}/backups`
    );
    const arr = (data ?? []) as SlideBackup[];
    return arr.sort((a, b) => +b.timestamp - +a.timestamp);
  },

  /** Create a backup snapshot with a label — uses legacy make-backup endpoint */
  async createBackup(slideId: string, name: string): Promise<void> {
    // Lesson tasks don't have a backup endpoint — skip silently
    if (taskIds.has(slideId)) return;
    await apiClient.post(`/v3/slides/${slideId}/make-backup`, { name });
  },

  /** Rename an existing backup */
  async updateBackupName(backupId: string, name: string): Promise<void> {
    await apiClient.put('/v3/slides-backups', { id: backupId, name });
  },

  /** Delete a backup */
  async deleteBackup(backupId: string): Promise<void> {
    await apiClient.delete(`/v3/slides-backups/${backupId}`);
  },

  /** Restore module content from a backup (mirrors old updateSlide logic) */
  async restoreBackup(slideId: string, backup: SlideBackup): Promise<void> {
    const payload: Record<string, unknown> = { ...backup };
    delete payload.slideId;
    delete payload.timestamp;
    delete payload.updatedTimestamp;
    await apiClient.put('/v3/slides', { ...payload, id: slideId, hasChanges: true });
    invalidateContainersCache();
    invalidateSlidesCache();
  },

  /** Clone a module into a container */
  async clone(slideId: string, containerId?: string, name?: string): Promise<SlideModule> {
    const cloneName = name || 'Модуль (копия)';
    if (containerId) {
      try {
        const { data } = await apiClient.post<SlideModule>(
          `/v3/slides/${slideId}/clone`,
          { containerId, name: cloneName }
        );
        if (data && (data.id || (data as any)._id)) {
          invalidateContainersCache();
          invalidateSlidesCache(containerId);
          return data;
        }
      } catch {
        // Backend /v3/slides/:id/clone returned 404 — use reliable cloneSlideModule
      }
    }
    const { cloneSlideModule } = await import('../utils/moduleCloneUtils');
    const cloned = await cloneSlideModule(slideId, cloneName, containerId);
    invalidateContainersCache();
    invalidateSlidesCache(containerId);
    return cloned;
  },
};

export interface SlideBackup {
  id: string;
  slideId?: string;
  name?: string;
  timestamp: number;
  updatedTimestamp?: number;
  [key: string]: unknown;
}

export default SlidesAPI;
